import type { CustomizationParams, RedirectSettings } from '../types';

export interface ParsedConfig {
  creatorId: string;
  productId: string;
  bumpProductIds?: string[];
  couponCode?: string;
  affiliateCode?: string;
  checkoutSessionSecret: string;
  environment: 'sandbox' | 'production';
  overrideBaseUrl?: string;
  theme?: CustomizationParams;
  containerOptions?: {
    width?: string;
    height?: string;
  };
  redirectSettings?: RedirectSettings;
  showSubmitButton?: boolean;
}

/**
 * Parse configuration from HTML data attributes
 */
export function parseDataAttributes(element: HTMLElement): ParsedConfig {
  const config: Partial<ParsedConfig> = {};

  // Required attributes
  if (element.dataset['creatorId']) {
    config.creatorId = element.dataset['creatorId'];
  }

  if (element.dataset['productId']) {
    config.productId = element.dataset['productId'];
  }

  if (element.dataset['checkoutSessionSecret']) {
    config.checkoutSessionSecret = element.dataset['checkoutSessionSecret'];
  }

  // Optional core attributes
  if (element.dataset['bumpProductIds']) {
    config.bumpProductIds = element.dataset['bumpProductIds'].split(',').map(id => id.trim());
  }

  if (element.dataset['couponCode']) {
    config.couponCode = element.dataset['couponCode'];
  }

  if (element.dataset['affiliateCode']) {
    config.affiliateCode = element.dataset['affiliateCode'];
  }

  if (element.dataset['environment']) {
    config.environment = element.dataset['environment'] as 'sandbox' | 'production';
  }

  if (element.dataset['overrideBaseUrl']) {
    config.overrideBaseUrl = element.dataset['overrideBaseUrl'];
  }

  if (element.dataset['showSubmitButton']) {
    config.showSubmitButton = element.dataset['showSubmitButton'] === 'true';
  }

  // Container options
  if (element.dataset['containerWidth'] || element.dataset['containerHeight']) {
    config.containerOptions = {};
    if (element.dataset['containerWidth']) {
      config.containerOptions.width = element.dataset['containerWidth'];
    }
    if (element.dataset['containerHeight']) {
      config.containerOptions.height = element.dataset['containerHeight'];
    }
  }

  // Redirect settings
  if (element.dataset['successRedirectUrl'] || element.dataset['failureRedirectUrl'] || element.dataset['alwaysRedirect']) {
    config.redirectSettings = {};
    if (element.dataset['successRedirectUrl']) {
      config.redirectSettings.success_redirect_url = element.dataset['successRedirectUrl'];
    }
    if (element.dataset['failureRedirectUrl']) {
      config.redirectSettings.failure_redirect_url = element.dataset['failureRedirectUrl'];
    }
    if (element.dataset['alwaysRedirect']) {
      config.redirectSettings.always_redirect = element.dataset['alwaysRedirect'] === 'true';
    }
  }

  // Theme attributes
  if (element.dataset['theme'] || element.dataset['showProductInfo'] || element.dataset['productLayout'] || 
      element.dataset['showCouponRow'] || element.dataset['couponRowDisclaimer'] || element.dataset['accentColor'] || element.dataset['backgroundColor'] ||
      element.dataset['labelColor'] || element.dataset['inputBackgroundColor'] || element.dataset['productTextColor'] ||
      element.dataset['headingColor'] || element.dataset['secondaryColor'] || element.dataset['borderColor'] ||
      element.dataset['surfaceColor'] || element.dataset['billingDisplayFields'] || element.dataset['billingFormPlacement'] ||
      element.dataset['productImage']) {
    
    config.theme = {
      theme: (element.dataset['theme'] as 'light' | 'dark') || 'light',
      show_product_info: element.dataset['showProductInfo'] === 'true',
      product_layout: (element.dataset['productLayout'] as 'left' | 'above') || 'left',
      show_coupon_row: element.dataset['showCouponRow'] === 'true',
      ...(element.dataset['couponRowDisclaimer'] !== undefined && {
        coupon_row_disclaimer: element.dataset['couponRowDisclaimer'],
      }),
      accent_color: element.dataset['accentColor'] || '#007bff',
      ...(element.dataset['backgroundColor'] && { background_color: element.dataset['backgroundColor'] }),
      ...(element.dataset['labelColor'] && { label_color: element.dataset['labelColor'] }),
      ...(element.dataset['inputBackgroundColor'] && { input_background_color: element.dataset['inputBackgroundColor'] }),
      ...(element.dataset['productTextColor'] && { product_text_color: element.dataset['productTextColor'] }),
      ...(element.dataset['headingColor'] && { heading_color: element.dataset['headingColor'] }),
      ...(element.dataset['secondaryColor'] && { secondary_color: element.dataset['secondaryColor'] }),
      ...(element.dataset['borderColor'] && { border_color: element.dataset['borderColor'] }),
      ...(element.dataset['surfaceColor'] && { surface_color: element.dataset['surfaceColor'] }),
      ...(element.dataset['productImage'] && { product_image: element.dataset['productImage'] }),
      ...(element.dataset['billingDisplayFields'] && { billing_display_fields: element.dataset['billingDisplayFields'] }),
      ...(element.dataset['billingFormPlacement'] && { billing_form_placement: element.dataset['billingFormPlacement'] as 'above' | 'left' })
    };
  }

  config.containerOptions = {
    width: '100%',
    height: '100%'
  };

  return config as ParsedConfig;
}

/**
 * Validate parsed configuration
 */
export function validateParsedConfig(config: ParsedConfig): boolean {
  return !!(config.creatorId && config.productId && config.checkoutSessionSecret);
}

/**
 * Auto-initialize checkout elements on the page
 */
export function autoInitialize(selector = '[data-checkout]'): void {
  const elements = document.querySelectorAll(selector);
  
  elements.forEach((element) => {
    if (element instanceof HTMLElement) {
      try {
        const config = parseDataAttributes(element);
        
        if (!validateParsedConfig(config)) {
          console.warn('Invalid checkout configuration for element:', element);
          return;
        }

        // Import and create checkout
        import('../PaymentCheckout').then(({ PaymentCheckout }) => {
          PaymentCheckout.fromElement(element, config);
          console.log('Checkout initialized for element:', element);
        }).catch((error) => {
          console.error('Failed to initialize checkout:', error);
        });
      } catch (error) {
        console.error('Error parsing checkout attributes:', error);
      }
    }
  });
}

/**
 * Check if an element has valid checkout attributes
 */
export function hasValidCheckoutAttributes(element: HTMLElement): boolean {
  try {
    const config = parseDataAttributes(element);
    return validateParsedConfig(config);
  } catch {
    return false;
  }
} 